import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isTransponder } from '@racemap/utilities/functions/starters/starters';
import type { RacemapStarter } from '@racemap/utilities/types/events';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { Immutable } from 'immer';
import type { CSSProperties, FC } from 'react';
import { useMemo, useState } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import { useReadCountsByTransponderIds } from '../../lib/customHooks/useReadCountsByTransponderIds';
import { Timestamp } from '../BasicComponents';
import { AutoHeightTable } from '../BasicComponents/AutoHeightTable';
import { SearchBar } from '../BasicComponents/SearchBar/SearchBar';
import { TooltipWithUTCDatetime } from '../BasicComponents/Tooltips/TooltipWithUTCDatetime';
import { isValidStartOrFinishTime } from '../utils/isValidStartOrFinishTime';
import { compareDates } from '../utils/tableColumns';
import { PredictionStarterAnalyseLink } from './AnalyseComponents/PredictionStarterAnalyseLink';
import { TransponderId } from './TransponderId';

type Transponder = RacemapStarter & { readsCount: number };

export const TranspondersSubTab: FC = () => {
  const event = useCurrentEvent();
  const [searchValue, setSearchValue] = useState('');

  const chipIds = useMemo(
    () =>
      Array.from(event?.starters?.values() ?? [])
        .filter((s) => isTransponder(s))
        .map((s) => s.appId)
        .filter((id): id is string => id != null && id !== ''),
    [event?.starters],
  );

  const readCountsByTransponderIds = useReadCountsByTransponderIds(event?.id, chipIds);

  const transponders: Array<Transponder> = Array.from(event?.starters?.values() ?? [])
    .filter((s: RacemapStarter) => s.appId != null && isTransponder(s))
    .map((t) => ({
      ...t,
      readsCount: readCountsByTransponderIds.get(t.appId ?? '') ?? 0,
    }));

  const searchedTransponders =
    searchValue === ''
      ? transponders
      : transponders.filter((t) =>
          [t.appId, t.name, t.startNumber, t.readsCount]
            .filter((v) => v != null)
            .some((v) => String(v).toLowerCase().includes(searchValue.toLowerCase())),
        );

  const columns: ColumnsType<Transponder> = [
    {
      key: 'index',
      title: '#',
      width: 60,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      key: 'appId',
      title: 'Transponder',
      width: 160,
      align: 'center',
      render: (_, transponder) => <TransponderId id={transponder.appId ?? null} />,
    },
    {
      key: 'name',
      title: 'Participant',
      width: 240,
      render: (_, transponder) => (
        <div style={{ marginLeft: 10 }}>
          <b>{transponder.startNumber}</b> {transponder.name}
        </div>
      ),
    },
    {
      key: 'readsCount',
      title: 'Reads Count',
      width: 130,
      align: 'center',
      render: (_, transponder) => transponder.readsCount,
    },
    eventTimeColumn('Start Time', (t) => t.times?.start, event),
    eventTimeColumn('Finish Time', (t) => t.times?.end, event),
    {
      key: 'action',
      title: 'Action',
      width: 100,
      align: 'center',
      render: (_, transponder) => {
        if (event?.id == null) return null;
        return <PredictionStarterAnalyseLink starterId={transponder.id} eventId={event.id} />;
      },
    },
  ];

  const scrollX = columns.reduce((sum, col) => sum + Number(col.width ?? 150), 0);

  return (
    <Container>
      <SearchBarContainer>
        <SearchBar value={searchValue} onChange={setSearchValue} />
      </SearchBarContainer>
      <TableArea>
        <AutoHeightTable<Transponder>
          dataSource={searchedTransponders}
          columns={columns}
          rowKey={(transponder) => transponder.id}
          scroll={{ x: scrollX }}
        />
      </TableArea>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
`;

const SearchBarContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 7px;

  .input-group {
    width: 400px;
  }
`;

const TableArea = styled.div`
  flex: 1;
  min-height: 0;
`;

function eventTimeColumn(
  label: string,
  getTime: (transponder: Transponder) => Date | string | null | undefined,
  event: Immutable<CurrentEvent> | null | undefined,
): ColumnType<Transponder> {
  return {
    key: label,
    title: label,
    width: 170,
    align: 'center',
    sorter: (a, b) => compareDates(getTime(a), getTime(b)),
    render: (_, transponder) => {
      const time = getTime(transponder);
      if (time == null) return null;

      const isValidTime = isValidStartOrFinishTime(time, event);
      const style: CSSProperties = {
        color: isValidTime ? 'green' : 'red',
      };
      const title = isValidTime
        ? 'The registered time is during the event!'
        : 'The registered time is before/after the event!';

      return (
        <div style={style}>
          <TooltipWithUTCDatetime datetime={time} hint={title}>
            <Timestamp
              time={time}
              color={isValidTime ? RacemapColors.BaseGreen : RacemapColors.BaseRed}
              format="dayWithTime"
            />
          </TooltipWithUTCDatetime>
        </div>
      );
    },
  };
}
